import React, { useState, useEffect } from 'react';
import { warrantyService } from '../api/warrantyService';
import Table from '../components/Common/Table';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import Modal from '../components/Common/Modal';
import '../components/Common/Common.css';
import { Download } from 'lucide-react';

function Warranties() {
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [extendMonths, setExtendMonths] = useState(12);

  const fetchWarranties = async () => {
    try {
      setLoading(true);
      const response = await warrantyService.getWarranties({
        page: currentPage,
        search: searchTerm,
        per_page: 15,
      });
      setWarranties(response.data || []);
      setLastPage(response.last_page || 1);
    } catch (error) {
      console.error('Error fetching warranties:', error);
      setWarranties([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarranties();
  }, [currentPage, searchTerm]);

  const handleExtend = async () => {
    try {
      await warrantyService.extendWarranty(selectedWarranty.id, extendMonths);
      setShowExtendModal(false);
      setExtendMonths(12);
      fetchWarranties();
    } catch (error) {
      console.error('Error extending warranty:', error);
      alert('Failed to extend warranty');
    }
  };

  const openExtendModal = (warranty) => {
    setSelectedWarranty(warranty);
    setExtendMonths(12);
    setShowExtendModal(true);
  };

  const handleDownloadCertificate = async (warranty) => {
    try {
      await warrantyService.downloadCertificate(warranty.id);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download warranty certificate');
    }
  };

  const columns = [
    { key: 'id', label: 'ID' },
    {
      key: 'uuid',
      label: 'Code garantie',
      render: (val) => (
        <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, letterSpacing: '0.5px' }}>
          {val || 'N/A'}
        </span>
      )
    },
    {
      key: 'product_name',
      label: 'Produit',
      render: (val, warranty) =>
        warranty?.sale_item?.product?.name || warranty?.order_item?.product?.name || 'N/A'
    },
    {
      key: 'source_document',
      label: 'Vente / Commande',
      render: (val, warranty) =>
        warranty?.sale_item?.sale?.invoice_uuid ||
        warranty?.order_item?.order?.invoice_number ||
        warranty?.order_item?.order?.invoice_uuid ||
        'N/A'
    },
    {
      key: 'start_date',
      label: 'Date de début',
      render: (val) => val ? new Date(val).toLocaleDateString('fr-FR') : 'N/A'
    },
    {
      key: 'end_date',
      label: 'Date de fin',
      render: (val) => val ? new Date(val).toLocaleDateString('fr-FR') : 'N/A'
    },
    {
      key: 'status',
      label: 'Statut',
      render: (val, warranty) => {
        if (!warranty?.end_date) return <span className="badge badge-secondary">Inconnu</span>;
        const endDate = new Date(warranty.end_date);
        const today = new Date();
        const isActive = endDate >= today;
        return (
          <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>
            {isActive ? 'Active' : 'Expirée'}
          </span>
        );
      }
    },
    {
      key: 'extended',
      label: 'Prolongée',
      render: (val) => <span className={`badge ${val ? 'badge-info' : 'badge-secondary'}`}>{val ? 'Oui' : 'Non'}</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (val, warranty) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadCertificate(warranty);
            }}
            className="btn btn-sm"
            style={{
              background: '#10b981',
              color: 'white',
              padding: '6px 12px',
              fontSize: '11px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
            title="Télécharger le certificat"
          >
            <Download size={14} />
            PDF
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Garanties</h1>
      </div>

      <div className="filters">
        <div className="filters-grid">
          <div style={{ gridColumn: 'span 2' }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher par code de garantie ou nom de produit..."
              onClear={() => setCurrentPage(1)}
            />
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={warranties}
        loading={loading}
        onEdit={openExtendModal}
        actions={true}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      <Modal
        isOpen={showExtendModal}
        onClose={() => setShowExtendModal(false)}
        title="Prolonger la garantie"
        size="small"
      >
        <div className="form-group">
          <label>Période de prolongation (Mois)</label>
          <input
            type="number"
            min="1"
            max="60"
            value={extendMonths}
            onChange={(e) => setExtendMonths(parseInt(e.target.value))}
          />
        </div>
        <div className="form-actions">
          <button onClick={() => setShowExtendModal(false)} className="btn btn-secondary">
            Annuler
          </button>
          <button onClick={handleExtend} className="btn btn-primary">
            Prolonger
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Warranties;
